import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { InvestmentCalculator } from '@/lib/investment-calculator'
import type { InvestmentWithRelations } from '@/types/investment'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { format } from 'date-fns'
import { 
  MoreHorizontal, 
  Download, 
  CheckCircle, 
  Clock,
  Star,
  User,
  Building,
  Edit,
  Trash,
  DollarSign
} from 'lucide-react'

interface InvestmentListProps {
  fundingRoundId: string
  projectId: string
  onEdit?: (investment: InvestmentWithRelations) => void
  onDelete?: (investmentId: string) => void
}

export default function InvestmentList({
  fundingRoundId,
  projectId,
  onEdit,
  onDelete
}: InvestmentListProps) {
  const [selectedInvestments, setSelectedInvestments] = useState<string[]>([])

  // Fetch investments
  const { data: investments, isLoading } = useQuery({
    queryKey: ['investments', fundingRoundId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('investments')
        .select(`
          *,
          investor:investor_id (
            id,
            first_name,
            last_name,
            email
          ),
          equity_class:equity_class_id (
            id,
            class_name
          ),
          created_by_user:created_by (
            first_name,
            last_name
          )
        `)
        .eq('funding_round_id', fundingRoundId)
        .order('investment_date', { ascending: false })

      if (error) throw error
      return (data || []) as unknown as InvestmentWithRelations[]
    }
  })

  // Calculate totals
  const totals = investments?.reduce((acc, inv) => {
    acc.committed += inv.investment_amount
    if (inv.wire_received_date) {
      acc.received += inv.investment_amount
    }
    acc.shares += inv.shares_purchased
    return acc
  }, { committed: 0, received: 0, shares: 0 }) || { committed: 0, received: 0, shares: 0 }

  const getInvestorName = (investment: InvestmentWithRelations) => {
    if (investment.investor) {
      return `${investment.investor.first_name} ${investment.investor.last_name}`
    }
    return investment.investor_name || 'Unknown'
  }

  const getInvestorEmail = (investment: InvestmentWithRelations) => {
    return investment.investor?.email || investment.investor_email || ''
  }

  const getInvestorTypeBadge = (type: string | null) => {
    const colors: Record<string, string> = {
      vc: 'bg-purple-100 text-purple-800',
      angel: 'bg-blue-100 text-blue-800',
      strategic: 'bg-green-100 text-green-800',
      employee: 'bg-yellow-100 text-yellow-800',
      other: 'bg-gray-100 text-gray-800'
    }

    const labels: Record<string, string> = {
      vc: 'VC',
      angel: 'Angel',
      strategic: 'Strategic',
      employee: 'Employee',
      other: 'Other'
    }

    const typeKey = type || 'other'
    return (
      <Badge className={colors[typeKey] || colors.other}>
        {labels[typeKey] || type}
      </Badge>
    )
  }

  const exportToCSV = () => {
    if (!investments) return

    const csvData = investments.map(inv => ({
      investor_name: getInvestorName(inv),
      investor_email: getInvestorEmail(inv),
      investor_type: inv.investor_type,
      investment_amount: inv.investment_amount,
      shares_purchased: inv.shares_purchased,
      price_per_share: inv.price_per_share,
      equity_class: inv.equity_class?.class_name || '',
      investment_date: format(new Date(inv.investment_date), 'yyyy-MM-dd'),
      wire_received_date: inv.wire_received_date ? format(new Date(inv.wire_received_date), 'yyyy-MM-dd') : '',
      is_lead_investor: inv.is_lead_investor ? 'Yes' : 'No',
      notes: inv.notes || ''
    }))

    const headers = Object.keys(csvData[0]).join(',')
    const rows = csvData.map(row => Object.values(row).join(','))
    const csv = [headers, ...rows].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `investments_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-gray-200 rounded w-full" />
        <div className="h-10 bg-gray-200 rounded w-full" />
        <div className="h-10 bg-gray-200 rounded w-full" />
      </div>
    )
  }

  if (!investments || investments.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No investments recorded yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-500">Total Committed</p>
          <p className="text-2xl font-semibold">
            {InvestmentCalculator.formatCurrency(totals.committed)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-500">Total Received</p>
          <p className="text-2xl font-semibold">
            {InvestmentCalculator.formatCurrency(totals.received)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {totals.committed > 0 
              ? `${Math.round((totals.received / totals.committed) * 100)}% collected`
              : '0% collected'
            }
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-500">Total Shares Issued</p>
          <p className="text-2xl font-semibold">
            {totals.shares.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportToCSV}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Investments Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Investor</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Shares</TableHead>
              <TableHead>Price/Share</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {investments.map((investment) => (
              <TableRow key={investment.id}>
                <TableCell>
                  <div>
                    <div className="flex items-center gap-2">
                      {investment.is_lead_investor && (
                        <Star className="h-4 w-4 text-yellow-500" fill="currentColor" />
                      )}
                      <span className="font-medium">{getInvestorName(investment)}</span>
                      {investment.investor_id ? (
                        <User className="h-3 w-3 text-gray-400" />
                      ) : (
                        <Building className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{getInvestorEmail(investment)}</div>
                  </div>
                </TableCell>
                <TableCell>
                  {getInvestorTypeBadge(investment.investor_type)}
                </TableCell>
                <TableCell className="font-medium">
                  {InvestmentCalculator.formatCurrency(investment.investment_amount)}
                </TableCell>
                <TableCell>
                  {investment.shares_purchased.toLocaleString()}
                </TableCell>
                <TableCell>
                  ${investment.price_per_share.toFixed(4)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {investment.equity_class?.class_name}
                  </Badge>
                </TableCell>
                <TableCell>
                  {format(new Date(investment.investment_date), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  {investment.wire_received_date ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      <span className="text-sm">Received</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-yellow-600">
                      <Clock className="h-4 w-4 mr-1" />
                      <span className="text-sm">Pending</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      {!investment.wire_received_date && (
                        <>
                          <DropdownMenuItem onClick={() => onEdit?.(investment)}>
                            <DollarSign className="h-4 w-4 mr-2" />
                            Mark as Received
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem onClick={() => onEdit?.(investment)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Investment
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onDelete?.(investment.id)}
                        className="text-red-600"
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete Investment
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Investment Notes */}
      {investments.some(inv => inv.notes) && (
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-gray-700">Investment Notes</h4>
          {investments.filter(inv => inv.notes).map(inv => (
            <div key={inv.id} className="bg-gray-50 p-3 rounded-lg text-sm">
              <div className="font-medium text-gray-700 mb-1">
                {getInvestorName(inv)}
              </div>
              <div className="text-gray-600">{inv.notes}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}